import { useEffect, useState, useContext, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box, Card, CardContent, Typography, Grid, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Button,
  Modal, IconButton, Tooltip,
} from "@mui/material";
import { IoIosWarning } from "react-icons/io";
import { CheckCircle, XCircle, Download, X, ChevronLeft, ChevronRight } from "lucide-react";
import BreadCrumb from "../../../../components/navbar/Breadcrumb";
import { getInstalacion } from "../../../../services";
import { formatoFechasEs } from "../../../../functions/funciones";
import { withRouter } from "../../../../providers/withRouter";
import { descargarCertificadoKae } from "../../../../functions/pdfs";
import { UsuarioContext } from "../../../../providers/userContext";

const InfoRow = ({ label, value }) => (
  <Box sx={{ display: "flex", py: 1, borderBottom: "1px solid #f0f0f0" }}>
    <Typography variant="body2" sx={{ fontWeight: 600, width: "40%", color: "text.secondary" }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ width: "60%" }}>
      {value || "—"}
    </Typography>
  </Box>
);

const Instalacion = () => {
  const { t } = useTranslation('common');
  const { id } = useParams();
  const user = useContext(UsuarioContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const imagenes = (data?.imagenes || []).map((img) => ({
    id: img.id,
    url: `${process.env.REACT_APP_STORAGE_URL}${img.ruta}`,
  }));

  const closeLightbox = () => setLightboxIdx(null);
  const prevImg = (e) => {
    e?.stopPropagation();
    setLightboxIdx((idx) => (idx === null ? null : (idx - 1 + imagenes.length) % imagenes.length));
  };
  const nextImg = (e) => {
    e?.stopPropagation();
    setLightboxIdx((idx) => (idx === null ? null : (idx + 1) % imagenes.length));
  };

  const swipeStartRef = useRef(null);
  const swipeWasGestureRef = useRef(false);
  const onSwipeStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    swipeStartRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
    swipeWasGestureRef.current = false;
  };
  const onSwipeEnd = (e) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    if (!start) return;
    const end = e.changedTouches?.[0];
    if (!end) return;
    const dx = end.clientX - start.x;
    const dy = end.clientY - start.y;
    const dt = Date.now() - start.time;
    const UMBRAL = 50;
    if (Math.abs(dx) > UMBRAL && Math.abs(dx) > Math.abs(dy) * 1.5 && dt < 800) {
      swipeWasGestureRef.current = true;
      if (dx > 0) prevImg(e);
      else nextImg(e);
    }
  };
  const onLightboxClick = () => {
    if (swipeWasGestureRef.current) {
      swipeWasGestureRef.current = false;
      return;
    }
    closeLightbox();
  };

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        prevImg();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        nextImg();
      } else if (e.key === "Escape") {
        e.preventDefault();
        closeLightbox();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIdx, imagenes.length]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await getInstalacion(id);
      setData(response);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: 40 }}><CircularProgress style={{ color: "#92004D" }} /></div>;
  }

  if (!data) {
    return <Typography sx={{ textAlign: "center", p: 4 }}>{t('installations.messages.noInstallations')}</Typography>;
  }

  const totalSenales = data.productos?.reduce((sum, p) => sum + (p.cantidad || 0), 0) || 0;

  return (
    <>
      <BreadCrumb
        rutas={[
          { path: "/instalaciones", label: t('installations.title') },
          { path: `/instalaciones/${id}`, label: t('installations.detail.title') },
        ]}
      />
      <div className="listPage">
        <div className="gridCard">
          <div className="gridHead">
            {t('installations.detail.title').toUpperCase()}
          </div>
        </div>
      </div>

      <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
        <Grid container spacing={3}>
          {/* Info general */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                  {t('installations.detail.info')}
                </Typography>
                <InfoRow label={t('installations.columns.nif')} value={data.propertie?.nif} />
                <InfoRow
                  label={t('installations.columns.community')}
                  value={
                    data.propertie?.razonSocial
                      ? (data.propertie?.nif
                          ? <Link className="detailLink" to={user.rol === 'AAFF' ? `/comunidades/${data.propertie.nif}` : `/aaff/${data.aaff?.nif}/comunidades/${data.propertie.nif}`}>{data.propertie.razonSocial}</Link>
                          : data.propertie.razonSocial)
                      : null
                  }
                />
                <InfoRow
                  label={t('installations.columns.aaff')}
                  value={
                    data.aaff?.razonSocial
                      ? (data.aaff?.nif
                          ? <Link className="detailLink" to={user.rol === 'AAFF' ? `/comunidades` : `/aaff/${data.aaff.nif}/comunidades`}>{data.aaff.razonSocial}</Link>
                          : data.aaff.razonSocial)
                      : null
                  }
                />
                <InfoRow label={t('installations.columns.address')} value={data.propertie?.direccion} />
                <InfoRow label={t('installations.columns.postalCode')} value={data.propertie?.codigoPostal} />
                <InfoRow label={t('installations.columns.invoiceDate')} value={data.invoiceDate ? formatoFechasEs(data.invoiceDate) : "—"} />
                <InfoRow label={t('installations.columns.invoiceDate2')} value={data.invoiceDate2 ? formatoFechasEs(data.invoiceDate2) : "—"} />
                <InfoRow
                  label={t('installations.columns.technician')}
                  value={data.technician ? `${data.technician.nombre} ${data.technician.apellidos}` : null}
                />
                <InfoRow label={t('installations.columns.performedDate')} value={data.realizada ? formatoFechasEs(data.fecha_instalacion) : "—"} />
                <Box sx={{ display: "flex", py: 1, alignItems: "center" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, width: "40%", color: "text.secondary" }}>
                    {t('installations.columns.done')}
                  </Typography>
                  <Box sx={{ width: "60%" }}>
                    {data.realizada
                      ? <Chip icon={<CheckCircle size={16} />} label={t('status.completed')} size="small" color="success" variant="outlined" />
                      : <Chip icon={<XCircle size={16} />} label={t('status.pending')} size="small" color="error" variant="outlined" />
                    }
                  </Box>
                </Box>
                <InfoRow label={t('installations.columns.signals')} value={totalSenales} />
                {data.realizada && (
                  <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      onClick={() => descargarCertificadoKae({
                        tecnicoNombre: data.technician ? `${data.technician.nombre} ${data.technician.apellidos}` : '',
                        ubicacionFinca: [data.propertie?.direccion, data.propertie?.codigoPostal, data.propertie?.municipio, data.propertie?.provincia].filter(Boolean).join(", "),
                        lugar: data.propertie?.municipio || data.propertie?.provincia || "",
                        fecha: data.fecha_instalacion,
                        nifPropiedad: data.propertie?.nif,
                      })}
                      variant="outlined"
                      startIcon={<Download size={16} />}
                      sx={{ color: "#92004D", borderColor: "#92004D", "&:hover": { borderColor: "#92004D", backgroundColor: "rgba(146,0,77,0.04)" } }}
                    >
                      {t('installations.detail.downloadCertificate')}
                    </Button>
                  </Box>
                )}
                {data.observaciones && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: "#f9f9f9", borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                      {t('installations.detail.observations')}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {data.observaciones}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Productos instalados */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                  {t('installations.detail.products')}
                </Typography>
                {data.productos && data.productos.length > 0 ? (
                  <TableContainer component={Paper} elevation={0}>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: "#92004D" }}>
                        <TableRow>
                          <TableCell sx={{ color: "#fff", fontSize: "0.8rem" }}>{t('installations.columns.product')}</TableCell>
                          <TableCell sx={{ color: "#fff", fontSize: "0.8rem" }}>{t('installations.columns.warehouse')}</TableCell>
                          <TableCell sx={{ color: "#fff", fontSize: "0.8rem" }} align="right">{t('installations.columns.quantity')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.productos.map((item) => (
                          <TableRow key={item.id} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500, display: "flex", alignItems: "center", gap: 0.5 }}>
                                {item.obligatorio && (
                                  <Tooltip title={t('installations.mandatorySignageItem')} arrow>
                                    <span style={{ color: "#b26a00", display: "inline-flex" }}>
                                      <IoIosWarning size={16} />
                                    </span>
                                  </Tooltip>
                                )}
                                {item.producto?.nombre}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {item.producto?.sku}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{item.almacen?.nombre}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: "bold" }}>{item.cantidad}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 4 }}>
                    {t('installations.detail.noProducts')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {imagenes.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                    {t('installations.images.title')} ({imagenes.length})
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1.5}>
                    {imagenes.map((img, idx) => (
                      <Box
                        key={img.id}
                        onClick={() => setLightboxIdx(idx)}
                        sx={{
                          width: { xs: "calc(50% - 6px)", sm: 120, md: 140 },
                          height: { xs: 130, sm: 120, md: 140 },
                          borderRadius: 1,
                          overflow: "hidden",
                          cursor: "pointer",
                          border: "1px solid #e0e0e0",
                          transition: "transform 0.15s",
                          "&:hover": { transform: "scale(1.03)", borderColor: "#92004D" },
                        }}
                      >
                        <img
                          src={img.url}
                          alt=""
                          loading="lazy"
                          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                        />
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>

      <Modal open={lightboxIdx !== null} onClose={closeLightbox} aria-labelledby="lightbox-instalacion">
        <Box
          onClick={onLightboxClick}
          onTouchStart={onSwipeStart}
          onTouchEnd={onSwipeEnd}
          sx={{
            position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
            backgroundColor: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center",
            outline: "none",
            touchAction: "pan-y",
            userSelect: "none",
          }}
        >
          {lightboxIdx !== null && imagenes[lightboxIdx] && (
            <>
              <IconButton
                onClick={closeLightbox}
                sx={{ position: "absolute", top: { xs: 12, sm: 16 }, right: { xs: 12, sm: 16 }, color: "#fff", backgroundColor: "rgba(255,255,255,0.15)", "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" } }}
              >
                <X size={24} />
              </IconButton>
              {imagenes.length > 1 && (
                <>
                  <IconButton
                    onClick={prevImg}
                    sx={{ position: "absolute", left: { xs: 8, sm: 16 }, color: "#fff", backgroundColor: "rgba(255,255,255,0.15)", "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" } }}
                  >
                    <ChevronLeft size={28} />
                  </IconButton>
                  <IconButton
                    onClick={nextImg}
                    sx={{ position: "absolute", right: { xs: 8, sm: 16 }, color: "#fff", backgroundColor: "rgba(255,255,255,0.15)", "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" } }}
                  >
                    <ChevronRight size={28} />
                  </IconButton>
                </>
              )}
              <img
                src={imagenes[lightboxIdx].url}
                alt=""
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: "92vw", maxHeight: "92vh", objectFit: "contain" }}
              />
            </>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default withRouter(Instalacion);
